import { ContentType, type CaptureEdgeKind } from '@vivport/backend/types';
import type { ObservationGroupInput } from '@/api/content-api';
import { useContentCreate } from './useContentQueries';

// A video is never analysed, so whatever title arrives here is the one the member
// keeps. The extension belongs to the stored key, not to what they read.
const stripExtension = (name: string): string => name.replace(/[.][^.]+$/, '');

interface CreateVideoOptions {
  /** The NeoChat this capture was submitted from; omitted for the manual door. */
  originProvenanceContentId?: number;
  linkToContentId?: number | null;
  observationGroup?: ObservationGroupInput | null;
  durationSeconds?: number | null;
}

interface UseVideoCreateResult {
  createVideo: (
    file: File,
    options?: CreateVideoOptions
  ) => Promise<{
    id: number;
    code: string;
    edgeKind: CaptureEdgeKind | null;
    observationGroupId: number | null;
  }>;
}

export function useVideoCreate(): UseVideoCreateResult {
  const createContentMutation = useContentCreate();

  const createVideo = async (file: File, options: CreateVideoOptions = {}) => {
    const { linkToContentId, observationGroup, durationSeconds, originProvenanceContentId } = options;
    // No AsyncQueue task: a video is never analysed, so there is nothing to poll for.
    const created = await createContentMutation.mutateAsync({
      contentType: ContentType.VIDEO,
      file,
      title: stripExtension(file.name),
      ...(originProvenanceContentId !== undefined ? { originProvenanceContentId } : {}),
      ...(linkToContentId ? { linkToContentId } : {}),
      ...(observationGroup ? { observationGroup } : {}),
      // HTMLMediaElement.duration is a float and can be Infinity while metadata loads;
      // the backend column is an int.
      ...(typeof durationSeconds === 'number' && Number.isFinite(durationSeconds)
        ? { durationSeconds: Math.round(durationSeconds) }
        : {}),
    });
    return {
      id: created.id,
      code: created.code,
      edgeKind: created.edgeKind ?? null,
      observationGroupId: created.observationGroupId ?? null,
    };
  };

  return { createVideo };
}
